// Commons imports.
import { SERVLET_COMMAND, ServletCmds } from '../../../NonReact/constants/ServletCommands'
import { SERVLET_ACTION, ServletActions } from '../../../NonReact/constants/React/ServletActions/ServletActions' // Sub imports.
import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { mapRequestResults } from '../../../NonReact/common/API/REST/RESTClient'
import { requestGetJsonURL } from '../../../NonReact/constants/ApiConstants'
import { JSONResponse, ReportListItemResponse } from '../../../NonReact/common/Types/Types'
import { RequestResults } from '../../../NonReact/common/Types/MainMenu/ReportListTypes'

// Response type param.
const RESPONSE_TYPE = 'ResType'

// Response type param value for JSON response.
const RESPONSE_TYPE_VALUE_JSON = 'JSON'

/**
 * Copies the report.
 *
 * @param servletPath Servlet path.
 * @param id Identifier of report to copy.
 */
export const copyReport = (id: string): Promise<JSONResponse<ReportListItemResponse>> =>
    requestGetJSON<RequestResults<ReportListItemResponse>>(
        requestGetJsonURL({
            [SERVLET_COMMAND]: ServletCmds.RPT_CHANGE,
            [SERVLET_ACTION]: ServletActions.COPY,
            ID: id,
            [RESPONSE_TYPE]: RESPONSE_TYPE_VALUE_JSON
        })
    ).then(mapRequestResults)

/**
 * Moves report to bin.
 *
 * @param servletPath Servlet path.
 * @param id Identifier of report to move to bin.
 */
export const moveReportToBin = (servletPath: string, id: string): Promise<ReportListItemResponse | undefined> => {
    const params: Record<string, string> = {
        [SERVLET_COMMAND]: ServletCmds.RPT_CHANGE,
        [SERVLET_ACTION]: ServletActions.REPORT_MOVE_TO_BIN,
        ID: id,
        [RESPONSE_TYPE]: RESPONSE_TYPE_VALUE_JSON
    }

    return requestGetJSON<RequestResults<ReportListItemResponse>>(requestGetJsonURL(params)).then(mapRequestResults)
}

/**
 * Restores report from bin.
 *
 * @param servletPath Servlet path.
 * @param id Identifier of report to move to bin.
 */
export const restoreReportFromBin = async (
    servletPath: string,
    id: string
): Promise<ReportListItemResponse | undefined> => {
    const params: Record<string, string> = {
        [SERVLET_COMMAND]: ServletCmds.RPT_CHANGE,
        [SERVLET_ACTION]: ServletActions.REPORT_RESTORE_FROM_BIN,
        ID: id,
        [RESPONSE_TYPE]: RESPONSE_TYPE_VALUE_JSON
    }

    // eslint-disable-next-line
    return await requestGetJSON<RequestResults<ReportListItemResponse>>(requestGetJsonURL(params))
        // eslint-disable-next-line
        .then(mapRequestResults)
}

/**
 * Deletes the report.
 *
 * @param servletPath Servlet path.
 * @param id Identifier of report to delete.
 */
export const deleteReport = async (servletPath: string, id: string): Promise<ReportListItemResponse | undefined> => {
    const params: Record<string, string> = {
        [SERVLET_COMMAND]: ServletCmds.RPT_CHANGE,
        [SERVLET_ACTION]: ServletActions.DELETE,
        ID: id,
        [RESPONSE_TYPE]: RESPONSE_TYPE_VALUE_JSON
    }

    // eslint-disable-next-line
    const response: Definable<ReportListItemResponse> = await requestGetJSON<RequestResults<ReportListItemResponse>>(
        requestGetJsonURL(params)
    )
        // eslint-disable-next-line
        .then(mapRequestResults)

    return response
}

/**
 * Deletes report that is in bin.
 *
 * @param servletPath Servlet path.
 * @param id Identifier of report to delete.
 */
export const deleteReportFromBin = async (
    servletPath: string,
    id: string
): Promise<ReportListItemResponse | undefined> => {
    const params: Record<string, string> = {
        [SERVLET_COMMAND]: ServletCmds.RPT_CHANGE,
        [SERVLET_ACTION]: ServletActions.REPORT_DELETE_FROM_BIN,
        ID: id,
        [RESPONSE_TYPE]: RESPONSE_TYPE_VALUE_JSON
    }

    // eslint-disable-next-line
    const response: Definable<ReportListItemResponse> = await requestGetJSON<RequestResults<ReportListItemResponse>>(
        requestGetJsonURL(params)
    )
        // eslint-disable-next-line
        .then(mapRequestResults)

    return response
}

/**
 * Renames folder.
 *
 * @param servletPath Servlet path.
 * @param currentName
 * @param newName
 */
export const renameFolder = async (
    servletPath: string,
    currentName: string,
    newName: string
): Promise<ReportListItemResponse | undefined> => {
    const params: Record<string, string> = {
        [SERVLET_COMMAND]: ServletCmds.RPT_FOLDER,
        [SERVLET_ACTION]: ServletActions.RENAME,
        NewPath: newName,
        CurrentPath: currentName,
        [RESPONSE_TYPE]: RESPONSE_TYPE_VALUE_JSON
    }

    return requestGetJSON<RequestResults<ReportListItemResponse>>(requestGetJsonURL(params)).then(mapRequestResults)
}

/**
 * Renames report.
 *
 * @param servletPath Servlet path.
 * @param reportID
 * @param newName
 */
export const renameReport = async (
    servletPath: string,
    reportID: string,
    newName: string
): Promise<ReportListItemResponse | undefined> => {
    const params: Record<string, string> = {
        [SERVLET_COMMAND]: ServletCmds.RPT_CHANGE,
        [SERVLET_ACTION]: ServletActions.REPORT_CHANGE,
        IsDescriptionChanged: 'TRUE',
        Description: newName,
        ID: reportID,
        [RESPONSE_TYPE]: RESPONSE_TYPE_VALUE_JSON
    }

    return requestGetJSON<RequestResults<ReportListItemResponse>>(requestGetJsonURL(params)).then(mapRequestResults)
}
